import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import React, {useMemo} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  ProcessTaskStatusType,
  ReviewStatus,
  StudySampleTestPlanNode,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import {ProcedureWithAnalysisTemplates} from 'procedures/shared-components/analysis/PreconfiguredAnalysisMissingTemplatesWarningBanner';
import {
  getFlowchartVersionIdForNavigation,
  getPowAgnosticProcedureFromFlowchartVersion,
  getPowAgnosticPublishedFlowchartVersionId,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRun,
} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {ProcessTaskStatus} from 'processes/__generated/ProcessTaskStatus';
import {ProcessTaskStatusDisplay} from 'processes/dashboard/ProcessTaskDashboardDisplay';
import {FilterKey, keyToEmptyFilter, SortKey} from 'search/SearchConstants';
import {ACCESSIBLE_ASSAY_RUN_COUNT_ON_OVERVIEW_PAGE} from 'studies/StudyConstants';
import {shouldShowProcedureForProcedureRunCreation} from 'studies/typed/shared/procedure-runs/procedure-selection/ProcedureDropdown';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {ChipSize} from 'ui-library/chips/Chip';
import ProcedureChip from 'ui-library/chips/ProcedureChip';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as RichObjectTable from 'ui-library/RichObjectTable';
import * as DateUtil from 'util/DateUtil';

import {RenderWorksheetChip} from '../shared/procedure-runs/overview/ProcedureRunTableUtil';
import AddAssayRunsButton from '../utils/ui/AddAssayRunsButton';
import StudyDesignTabButton from '../utils/ui/StudyDesignTabButton';
import StudyItemsTabButton from '../utils/ui/StudyItemsTabButton';
import {
  ClinicalBasedStudyExecutionOverviewQuery,
  ClinicalBasedStudyExecutionOverviewQuery_study_StudyNode_activeProcedureRuns_ProcedureRunNode as ProcedureRunNode,
  ClinicalBasedStudyExecutionOverviewQuery_study_StudyNode_activeProcedureRuns_ProcedureRunNode_methodExecutionInstances_ProcedureMethodExecutionInstanceNode as MethodExecutionInstance,
  ClinicalBasedStudyExecutionOverviewQuery_study_StudyNode_activeProcedureRuns_ProcedureRunNode_methodExecutionInstances_ProcedureMethodExecutionInstanceNode_methodTask_ProcessTaskNode_assignee_UserNode as Assignee,
  ClinicalBasedStudyExecutionOverviewQuery_study_StudyNode_studyDesign_TrialSampleTestingStudyDesignNode_sampleTestPlans_StudySampleTestPlanNode as SampleTestPlan,
} from './__generated/ClinicalBasedStudyExecutionOverviewQuery';
import ClinicalStudyOverviewTable, {renderEmptyRow} from './ClinicalStudyOverviewTable';

type StudyNode = ExtractByTypename<ClinicalBasedStudyExecutionOverviewQuery['study'], 'StudyNode'>;

/* exported for testing */
export const ASSAY_RUNS_TABLE_COLUMNS = [
  {
    name: 'Run name',
    width: 23,
  },
  {
    name: 'Created',
    width: 12,
  },
  {
    name: 'Created by',
    width: 11,
  },
  {
    name: 'Assignees',
    width: 10,
  },
  {
    name: 'Status',
    width: 9,
  },
  {
    name: 'Worksheet',
    width: 15,
  },
  {
    name: 'Review status',
    width: 8,
  },
  {
    name: 'Assay',
    width: 12,
  },
] as const;

export enum AssayRunEmptyState {
  /**
   * This state is used when there are no samples in the study.
   * Assay run table will not link to the design tab but display
   * an empty message.
   */
  HAS_NO_SAMPLES = 'has no uncategorized samples and no sample definitions',
  /**
   * This state is used when there are no sample definitions in the study
   * but has uncategorized samples. Assay run table will then link to the design tab
   * and display an empty message.
   */
  HAS_UNCATEGORIZED_SAMPLES = 'has uncategorized samples but no sample definitions',
  /**
   * This state is used when there are sample definitions in the study.
   * Assay run table will then display 'Add run' buttons.
   */
  HAS_SAMPLE_DEFINITIONS = 'has sample definitions',
}

interface AssayRunTableProps {
  study: StudyNode;
  assayRunEmptyState?: AssayRunEmptyState;
}

/*
 * Returns first accessible worksheet that is not in a terminal state.
 * If all accessible worksheets are in terminal states, returns the final accessible worksheet.
 * If no tasks are accessible, returns first worksheet.
 * If no worksheets are accessible, returns null.
 *
 * exported for testing
 */
export function getNextWorksheet(pmeis: Array<MethodExecutionInstance>): {
  id: string | null;
  status: ProcessTaskStatus | null;
  hasReviewLifecyclesAvailable: boolean | null;
  reviewStatus: ReviewStatus | null;
} {
  // Only PMEIs associated with worksheets with accessible task data and uniform task status are eligible
  const pmeisWithAccessibleWorksheetData = pmeis.filter(
    (pmei) =>
      !isInaccessible(pmei.worksheet) &&
      pmei.worksheet.tasks != null &&
      pmei.worksheet.tasks.length > 0 &&
      pmei.worksheet.tasks.every(
        (task) =>
          !isInaccessible(task) &&
          task.status ===
            (!isInaccessible(pmei.worksheet) &&
              pmei.worksheet.tasks?.[0] &&
              !isInaccessible(pmei.worksheet.tasks[0]) &&
              pmei.worksheet.tasks[0].status)
      )
  );

  for (let i = 0; i < pmeisWithAccessibleWorksheetData.length; i++) {
    const pmei = pmeisWithAccessibleWorksheetData[i];
    // We only assert and check the first task below since all the tasks for PMEIs that we are iterating over here
    // are guaranteed to have the same status due to the filter above
    assert(
      !isInaccessible(pmei.worksheet) &&
        pmei.worksheet.tasks[0] != null &&
        !isInaccessible(pmei.worksheet.tasks[0])
    );
    // returns first accessible worksheet that is not in a terminal state or last worksheet if all are in terminal states
    if (
      pmei.worksheet.tasks[0].status.statusType === ProcessTaskStatusType.PENDING ||
      pmei.worksheet.tasks[0].status.statusType === ProcessTaskStatusType.IN_PROGRESS ||
      i === pmeisWithAccessibleWorksheetData.length - 1
    ) {
      return {
        id: pmei.worksheet.id,
        status: pmei.worksheet.tasks[0].status,
        hasReviewLifecyclesAvailable: pmei.worksheet.parentReviewLifecycles.length > 0,
        reviewStatus: pmei.worksheet.review?.status ?? null,
      };
    }
  }
  // if tasks are inaccessible but worksheets are accessible, returns first worksheet id
  // this is usually the case for newly created assay runs and therefore the first render of the worksheet chip cell
  return {
    id: isInaccessible(pmeis[0].worksheet) ? null : pmeis[0].worksheet.id,
    status: null,
    hasReviewLifecyclesAvailable: null,
    reviewStatus: null,
  };
}

/*
 * Returns the assignees for the given pmeis if they have a method task and the method task is accessible
 *
 * exported for testing
 */
export function getAssignees(pmeis: Array<MethodExecutionInstance>): Array<Assignee> {
  const assignees: Array<Assignee> = [];
  pmeis.forEach((pmei) => {
    if (pmei.methodTask != null && !isInaccessible(pmei.methodTask) && pmei.methodTask.assignee != null) {
      assignees.push(pmei.methodTask.assignee);
    }
  });
  return assignees;
}

type AccessibleSampleTestPlan = StudySampleTestPlanNode & {procedure: ProcedureWithAnalysisTemplates};
export type SampleTestPlanProcedureTemplate = ProcedureWithAnalysisTemplates & {
  procedureFlowchartVersionId: string;
};

/*
 * Returns the procedure templates that are accessible to render in the procedure dropdown component.
 * Excludes any archived procedures.
 *
 * exported for testing
 */
export function getProcedureTemplatesToRender(
  sampleTestPlans: Array<SampleTestPlan>
): Array<SampleTestPlanProcedureTemplate> {
  const allowedProcedureTemplates: Array<SampleTestPlanProcedureTemplate> = [];
  const savedProcedureTemplateIds = new Set<string>();
  const accessibleSampleTestPlans: Array<AccessibleSampleTestPlan> = sampleTestPlans.filter(
    (sampleTestPlan): sampleTestPlan is AccessibleSampleTestPlan =>
      !isInaccessible(sampleTestPlan.procedure) && sampleTestPlan.isReadyForExecution
  );
  for (const sampleTestPlan of accessibleSampleTestPlans) {
    // allows only unique, unarchived, published procedure templates to render in the procedure dropdown component
    if (
      !savedProcedureTemplateIds.has(sampleTestPlan.procedure.id) &&
      !sampleTestPlan.procedure.archiveData.archived &&
      shouldShowProcedureForProcedureRunCreation(sampleTestPlan.procedure)
    ) {
      const procedureFlowchartVersionId = getPowAgnosticPublishedFlowchartVersionId(sampleTestPlan.procedure);

      assert(
        procedureFlowchartVersionId,
        'Expected a procedure flowchart version ID. Cannot create runs without a flowchart version.'
      );

      allowedProcedureTemplates.push({
        ...sampleTestPlan.procedure,
        procedureFlowchartVersionId,
      });
      savedProcedureTemplateIds.add(sampleTestPlan.procedure.id);
    }
  }
  return allowedProcedureTemplates;
}

export default function AssayRunsTable(props: AssayRunTableProps): JSX.Element {
  let allowedProcedureTemplates: Array<SampleTestPlanProcedureTemplate> = [];
  if (props.study.studyDesign?.sampleTestPlans != null) {
    allowedProcedureTemplates = getProcedureTemplatesToRender(props.study.studyDesign.sampleTestPlans);
  }
  const accessibleAssayRuns = props.study.activeProcedureRuns.filter((run): run is ProcedureRunNode => {
    if (isInaccessible(run)) {
      return false;
    }
    const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(run);
    return !isInaccessible(flowchartVersion);
  });
  // we query for the top ACCESSIBLE_ASSAY_RUN_COUNT_ON_OVERVIEW_PAGE + 1 assay runs to condiditionally
  // render the "See all assay runs" link, but we only want to render the top
  // ACCESSIBLE_ASSAY_RUN_COUNT_ON_OVERVIEW_PAGE assay runs in the table
  const accessibleAssayRunsToRender = useMemo(
    () => accessibleAssayRuns.slice(0, ACCESSIBLE_ASSAY_RUN_COUNT_ON_OVERVIEW_PAGE),
    [accessibleAssayRuns]
  );
  const worksheetDataByRunId = useMemo(
    () =>
      new Map<string, ReturnType<typeof getNextWorksheet>>(
        accessibleAssayRunsToRender.map((run) => [run.id, getNextWorksheet(run.methodExecutionInstances)])
      ),
    [accessibleAssayRunsToRender]
  );
  const showReviewColumn = useMemo(
    () =>
      BenchlingConfig.ENABLE_WORKSHEET_REVIEWS &&
      Array.from(worksheetDataByRunId.values()).some((run) => run.hasReviewLifecyclesAvailable),
    [worksheetDataByRunId]
  );
  const assayRunEmptyState = props.assayRunEmptyState ?? AssayRunEmptyState.HAS_SAMPLE_DEFINITIONS;

  const getEmptyRow = (assayRunEmptyState: AssayRunEmptyState): JSX.Element => {
    let message = '';
    let children;
    switch (assayRunEmptyState) {
      case AssayRunEmptyState.HAS_NO_SAMPLES:
        message = 'Assay runs will be available once samples are defined';
        break;
      case AssayRunEmptyState.HAS_UNCATEGORIZED_SAMPLES:
        message = 'Assay runs will be available once samples are defined';
        children = (
          <StudyDesignTabButton studyId={props.study.id} studyType={props.study.studySchema.studyType} />
        );
        break;
      case AssayRunEmptyState.HAS_SAMPLE_DEFINITIONS:
        message = 'Add assay runs to test your samples';
        children = (
          <AddAssayRunsButton
            study={{
              ...props.study,
              procedureRuns: props.study.activeProcedureRuns,
              allowedProcedureTemplates,
            }}
            buttonSize={ButtonSize.SMALL}
          />
        );
        break;
      default:
        assert(false, 'Unexpected assay run empty state');
    }
    return renderEmptyRow(message, children);
  };

  const assayTableColumns = ASSAY_RUNS_TABLE_COLUMNS.filter((c) => {
    switch (c.name) {
      case 'Review status':
        return showReviewColumn;
      default:
        return true;
    }
  });

  const getAssayTableRows = (): Array<JSX.Element> => {
    const rows: Array<JSX.Element> = [];
    accessibleAssayRunsToRender.forEach((run) => {
      const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(run);
      assert(!isInaccessible(flowchartVersion), 'Expected flowchart version to be accessible.');
      const flowchartVersionId = getFlowchartVersionIdForNavigation(flowchartVersion);
      const procedureParent = getPowAgnosticProcedureFromFlowchartVersion(flowchartVersion);

      const name = (
        <RichObjectTable.Cell className={css(styles.cellContainer)}>
          <div className={css(styles.cellText)}>{run.name}</div>
        </RichObjectTable.Cell>
      );
      const createdAt = (
        <RichObjectTable.Cell className={css(styles.cellContainer)}>
          <div className={css(styles.cellText)}>{DateUtil.readableTime(run.createdAt, {showTime: true})}</div>
        </RichObjectTable.Cell>
      );
      const createdBy = (
        <RichObjectTable.Cell className={css(styles.cellContainer)}>
          <div className={css(styles.createdBy)}>
            <Avatar
              entity={{
                name: run.creator.name,
                avatarUrl: run.creator.avatarUrl,
                handle: run.creator.handle,
              }}
              size={AvatarSize.SMALL}
              showTooltip={false}
            />
            <div className={css(styles.marginBetweenComponents)}>{run.creator.name}</div>
          </div>
        </RichObjectTable.Cell>
      );
      const assigneesData = getAssignees(run.methodExecutionInstances);
      const assignees = (
        <RichObjectTable.Cell className={css(styles.cellContainer)}>
          {assigneesData.slice(0, 2).map((assignee, index) => (
            <div className={css(styles.paddingBetweenAvatars)} key={index}>
              <Avatar
                entity={{
                  name: assignee.name,
                  handle: assignee.handle,
                  avatarUrl: assignee.avatarUrl,
                }}
                size={AvatarSize.SMALL}
                showTooltip={true}
              />
            </div>
          ))}
          {assigneesData.length > 2 && (
            <div className={css(styles.paddingBetweenAvatars)} key={assigneesData.length}>
              <Tooltip
                text={assigneesData
                  .slice(2)
                  .map((assignee) => assignee.name)
                  .join(', ')}
                placement={FloatingPlacement.TOP}
              >
                <BenchlingIcon icon="team" className={css(styles.icon)} />
              </Tooltip>
            </div>
          )}
        </RichObjectTable.Cell>
      );
      const worksheetData = worksheetDataByRunId.get(run.id) ?? {
        id: null,
        status: null,
        hasReviewLifecyclesAvailable: null,
        reviewStatus: null,
      };
      const status = (
        <RichObjectTable.Cell className={css(styles.cellContainer)}>
          {worksheetData.status == null ? null : <ProcessTaskStatusDisplay status={worksheetData.status} />}
        </RichObjectTable.Cell>
      );
      const worksheet = (
        <RichObjectTable.Cell className={css(styles.cellContainer)}>
          <RenderWorksheetChip worksheetId={worksheetData.id} />
        </RichObjectTable.Cell>
      );
      const maybeWorksheetReviewStatus = showReviewColumn && (
        <RichObjectTable.Cell className={css(styles.cellContainer)}>
          <div className={css(styles.cellText)}>
            {worksheetData.hasReviewLifecyclesAvailable
              ? ReviewUtil.getStatusTextFromReview(worksheetData.reviewStatus ?? undefined)
              : 'N/A'}
          </div>
        </RichObjectTable.Cell>
      );
      const assayTemplate = (
        <RichObjectTable.Cell className={css(styles.cellContainer)}>
          <ProcedureChip
            id={procedureParent.id}
            flowchartVersionId={flowchartVersionId}
            size={ChipSize.MINI}
          />
        </RichObjectTable.Cell>
      );
      rows.push(
        <RichObjectTable.Row key={rows.length}>
          {name}
          {createdAt}
          {createdBy}
          {assignees}
          {status}
          {worksheet}
          {maybeWorksheetReviewStatus}
          {assayTemplate}
        </RichObjectTable.Row>
      );
    });
    if (rows.length === 0) {
      rows.push(getEmptyRow(assayRunEmptyState));
    }
    return rows;
  };
  return (
    <div>
      <ClinicalStudyOverviewTable
        title="Assay runs"
        rightHeader={
          accessibleAssayRuns.length > ACCESSIBLE_ASSAY_RUN_COUNT_ON_OVERVIEW_PAGE ? (
            <div className={css(styles.runDetailsLink)}>
              <StudyItemsTabButton
                studyId={props.study.id}
                style={ButtonStyle.TEXT}
                additionalFilters={[
                  {
                    ...keyToEmptyFilter(FilterKey.TYPES),
                    value: [ModelTypeConstants.ASSAY_RUN_TASK],
                  },
                ]}
                sorts={[{key: SortKey.SORT_STATUS, reverse: false}]}
                text="See all assay runs"
              />
            </div>
          ) : undefined
        }
        columnNames={accessibleAssayRunsToRender.length > 0 ? assayTableColumns : []}
        tableRows={getAssayTableRows()}
        titleButton={
          assayRunEmptyState === AssayRunEmptyState.HAS_SAMPLE_DEFINITIONS ? (
            <AddAssayRunsButton
              study={{
                ...props.study,
                procedureRuns: props.study.activeProcedureRuns,
                allowedProcedureTemplates,
              }}
            />
          ) : null
        }
      />
    </div>
  );
}

const styles = StyleSheet.create({
  noRunsContainer: {
    textAlign: 'center',
  },
  noRunsText: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    marginBottom: 7,
  },
  noRunsBackground: {
    backgroundColor: Color.GRAY_50,
    height: 100,
  },
  cellContainer: {
    paddingBottom: 3,
    paddingTop: 3,
    paddingLeft: 12,
  },
  cellText: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: Color.GRAY_900,
    lineHeight: '26px',
  },
  createdBy: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: Color.GRAY_900,
    lineHeight: '26px',
    display: 'flex',
  },
  marginBetweenComponents: {
    marginLeft: 5,
  },
  paddingBetweenAvatars: {
    paddingLeft: 5,
    display: 'inline-block',
  },
  icon: {
    marginLeft: 3,
    display: 'inline-block',
  },
  runDetailsLink: {
    display: 'inline-block',
  },
});
